import { FC, useEffect, useRef, useState } from 'react';

import { CopyRichTextButton } from '@eluve/blocks';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DetailsSidebarProvider,
  Heading,
  Icon,
  IconName,
  Separator,
  Stack,
  Text,
  cn,
  useDetailsSidebar,
  useToast,
} from '@eluve/components';
import {
  TRANSCRIPT_TAB,
  useAppointmentActivity,
  useAppointmentId,
  useAppointmentPatient,
  useAppointmentSummaryDisplayKind,
  useAppointmentTabNav,
  useIsAppointmentReadonly,
  usePendingUploadDevices,
  useSummary,
} from '@eluve/frontend-appointment-hooks';
import {
  AppointmentBillingCodes,
  AppointmentLanguageSelector,
  DynamicSummaryRenderer,
  FHIRMedicineRecommendationsContainer,
  ReferToSpecialist,
  RegenerateSummaryButton,
  SummaryStuck,
  getSummarizationErrorMessage,
} from '@eluve/frontend-feature-appointment';
import { LlmOutputFailureTypesEnum } from '@eluve/graphql-types';
import { useTranslation } from '@eluve/i18n';
import { extractSummaryMarkdownContent } from '@eluve/llm-outputs';
import { ALL_MINI_APPS, MiniAppActions } from '@eluve/mini-apps';
import {
  useCaptureEvent,
  useIsPostHogFeatureFlagEnabled,
} from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useSession,
} from '@eluve/session-helpers';
import {
  CustomInstructions,
  FeatureFlaggedComponent,
  RollbackPreviousSummaryButton,
  SummaryFeedbackModal,
  TenantOrUserFeatureFlaggedComponent,
} from '@eluve/smart-blocks';
import { useTriggerLocalFileReupload } from '@eluve/user-local-files';
import { formatNaturalList, getUserName } from '@eluve/utils';

import { SummarizationErrorCard } from '../SummarizationErrorCard';

import { pendingUnsavedChangesStore } from './AppointmentLastSaved';
import { HiddenForManualCharting } from './HiddenForManualCharting';
import { DifferentialDiagnosesComponent } from './Insights/DifferentialDiagnosesComponent';

const SummaryPendingData: FC = () => {
  const { t } = useTranslation();
  const appointmentId = useAppointmentId();
  const hasTrackedEvent = useRef(false);
  const pendingDevices = usePendingUploadDevices();
  const tenantId = useAssignedTenantIdFromParams();

  const captureEvent = useCaptureEvent({
    tenantId,
    appointmentId,
  });

  useEffect(() => {
    if (pendingDevices.length > 0 && !hasTrackedEvent.current) {
      captureEvent('summary_pending_displayed_with_devices', {
        pendingDeviceCount: pendingDevices.length,
        pendingDevices,
      });
      hasTrackedEvent.current = true;
    }
  }, [pendingDevices, captureEvent]);

  const browsers = pendingDevices.map((device) =>
    t('appointmentPage.summaryStates.browserOnDevice', {
      browser: device.browserName,
      device: device.model ?? device.operatingSystem,
    }),
  );

  return (
    <Stack
      align="center"
      className="max-w-[500px] self-center pt-8 text-center"
      gap={8}
    >
      <Heading size="2xl">
        {t('appointment.summaryStates.pendingDataTitle')}
      </Heading>
      <Text className="max-w-[400px]" color="tertiary" size="md">
        {t('appointmentPage.summaryStates.pendingDataDescription')}
      </Text>
      {browsers.length > 0 && (
        <Stack align="center" className="mt-8" gap={4}>
          <div className="rounded-lg bg-bg-negative-subtle p-4 text-text-negative-subtle">
            <Icon name="CloudUpload" size="xl" />
          </div>
          <Text className="text-text-negative" size="md">
            {browsers.length === 1
              ? t('appointmentPage.summaryStates.uploadNeededPrefixOne')
              : t('appointmentPage.summaryStates.uploadNeededPrefixMany')}
            <Text color="inherit" size="md" weight="bold" inline>
              {` ${formatNaturalList(browsers)}`}
            </Text>
            {browsers.length === 1
              ? t('appointmentPage.summaryStates.uploadNeededSuffixOne')
              : t('appointmentPage.summaryStates.uploadNeededSuffixMany')}
          </Text>
        </Stack>
      )}
    </Stack>
  );
};

const SummaryInProgress = () => {
  const { t } = useTranslation();
  return (
    <Stack
      align="center"
      className="max-w-[500px] self-center pt-8 text-center"
      gap={8}
    >
      <Heading size="2xl">
        {t('appointment.summaryStates.inProgressTitle')}
      </Heading>
      <Text color="tertiary" size="md">
        {t('appointment.summaryStates.inProgressDescription')}
      </Text>
    </Stack>
  );
};

const SummaryInProgressTakingLonger = () => {
  const { t } = useTranslation();
  return (
    <Stack
      align="center"
      className="max-w-[500px] self-center pt-8 text-center"
      gap={8}
    >
      <Heading size="2xl">
        {t('appointment.summaryStates.takingLongerTitle')}
      </Heading>
      <Text color="tertiary" size="md">
        {t('appointmentPage.summaryStates.takingLongerDescription')}
      </Text>
    </Stack>
  );
};

const SummaryFailedVariantStyles = {
  error: {
    iconName: 'AlertCircle',
    iconBg: 'bg-bg-negative-subtle',
    iconColor: 'text-text-negative-subtle',
    titleColor: 'text-text-negative-subtle',
  },
  warning: {
    iconName: 'FileExclamationPoint',
    iconBg: 'bg-bg-tertiary',
    iconColor: 'text-text-primary',
    titleColor: 'text-text-primary',
  },
};

const SummaryFailed: React.FC<{
  failureType?: LlmOutputFailureTypesEnum | null;
}> = ({ failureType }) => {
  const { t } = useTranslation();
  const errorMessage = getSummarizationErrorMessage(failureType);
  const styles = SummaryFailedVariantStyles[errorMessage.variant];
  const { setTab } = useAppointmentTabNav();

  const isResumable =
    failureType === 'NO_MEDICAL_CONTEXT' ||
    failureType === 'TRANSCRIPT_TOO_SHORT';

  return (
    <Stack
      align="center"
      className="max-w-[500px] self-center pt-8 text-center"
      gap={8}
    >
      <Stack align="center" gap={4}>
        <div className={cn('rounded-lg p-4', styles.iconBg)}>
          <Icon
            name={styles.iconName as IconName}
            size="xl"
            className={styles.iconColor}
          />
        </div>
        <Heading size="2xl" className={styles.titleColor}>
          {t(errorMessage.titleKey)}
        </Heading>
      </Stack>
      <Text color="tertiary" size="md">
        {t(errorMessage.descriptionKey)}
      </Text>
      {isResumable && (
        <Button variant="ghost" onClick={() => setTab(TRANSCRIPT_TAB)}>
          {t('appointmentPage.summaryFailed.resumeRecording')}
        </Button>
      )}
      {errorMessage.showRegenerateHint && <RegenerateSummaryButton />}
    </Stack>
  );
};

const SummaryResult = () => {
  const { t } = useTranslation();
  const currentSummary = useSummary();
  const appointmentId = useAppointmentId();
  const contentRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const isReadonly = useIsAppointmentReadonly();
  const [instructions, setInstructions] = useState<string>('');
  const tenantId = useAssignedTenantIdFromParams();
  const session = useSession();
  const patient = useAppointmentPatient();

  const isReferralFlowEnabled = useIsPostHogFeatureFlagEnabled(
    'WEB_DEMO_REFERRAL_FLOW',
  );

  const physicianName = getUserName({
    firstName: session.firstName,
    lastName: session.lastName,
  });
  const patientName = getUserName({
    firstName: patient?.firstName,
    lastName: patient?.lastName,
  });

  const captureEvent = useCaptureEvent({
    tenantId,
    appointmentId,
  });

  const { setOpen } = useDetailsSidebar();

  if (!currentSummary.isSummaryAvailable) {
    return null;
  }

  const miniAppContext = {
    tenantId,
    appointmentId,
    patientId: patient?.id ?? undefined,
    hasGeneratedNote: currentSummary.isSummaryAvailable,
  };

  return (
    <Stack align="stretch">
      {!isReadonly && (
        <Stack
          justify="between"
          className="pb-5 lg:flex-row lg:items-center"
          gap={2}
        >
          <Stack gap={4} direction="horizontal" wrap>
            <HiddenForManualCharting>
              <RegenerateSummaryButton />
              <CustomInstructions
                instructions={instructions}
                setInstructions={setInstructions}
                disabled={currentSummary.isEmpty}
              />
              <FeatureFlaggedComponent flag="MEDPLUM">
                <MiniAppActions
                  slot="appointment-session"
                  apps={ALL_MINI_APPS}
                  context={miniAppContext}
                />
              </FeatureFlaggedComponent>
              <RollbackPreviousSummaryButton />
            </HiddenForManualCharting>

            <CopyRichTextButton
              getContentElement={() => contentRef.current}
              markdownContent={extractSummaryMarkdownContent(
                currentSummary.summary,
              )}
              hideContentInTooltip
              size="sm"
              variant="outline"
            >
              {t('appointment.summary.copySummary')}
            </CopyRichTextButton>
          </Stack>

          <div>
            <TenantOrUserFeatureFlaggedComponent flag="MULTILINGUAL_SUPPORT">
              <AppointmentLanguageSelector
                inline
                outputLanguage
                regenerateSummary
              />
            </TenantOrUserFeatureFlaggedComponent>
          </div>
        </Stack>
      )}

      <SummarizationErrorCard />

      <div ref={contentRef} className="w-full">
        {currentSummary.isSummaryAvailable && (
          <DynamicSummaryRenderer
            llmOutputId={currentSummary.llmOutputId}
            humanOutputId={currentSummary.humanOutputId}
            summary={currentSummary.summary!}
            onPendingChanges={() =>
              pendingUnsavedChangesStore.send({ type: 'set', value: true })
            }
            onSave={() =>
              pendingUnsavedChangesStore.send({ type: 'set', value: false })
            }
            onError={() => toast.error(t('appointmentPage.saveBlockFailed'))}
          />
        )}
      </div>

      {currentSummary.isSummaryAvailable && (
        <HiddenForManualCharting>
          <Stack
            className="rounded-lg p-4"
            direction="horizontal"
            align="center"
            justify="between"
            gap={2}
          >
            <SummaryFeedbackModal
              appointmentId={appointmentId}
              llmOutputId={currentSummary?.llmOutputId}
            />
          </Stack>
        </HiddenForManualCharting>
      )}

      <Separator className="my-7 bg-border-secondary" />

      <Stack gap={8} className="mb-10">
        <FeatureFlaggedComponent flag="INSIGHTS">
          <Stack gap={2}>
            <Heading size="xl" weight="semibold">
              {t('appointment.insights.differentialDiagnoses')}
            </Heading>
            <div className="w-full rounded-lg border border-border-primary bg-white p-6">
              <DifferentialDiagnosesComponent />
            </div>
          </Stack>
        </FeatureFlaggedComponent>

        <FeatureFlaggedComponent flag="BILLING_CODES">
          <AppointmentBillingCodes
            appointmentId={appointmentId}
            tenantId={tenantId}
          />
        </FeatureFlaggedComponent>

        <FeatureFlaggedComponent flag="MEDICATION">
          <FHIRMedicineRecommendationsContainer
            appointmentId={appointmentId}
            tenantId={tenantId}
          />
        </FeatureFlaggedComponent>

        {isReferralFlowEnabled && (
          <ReferToSpecialist
            physicianName={physicianName || undefined}
            patientName={patientName || undefined}
          />
        )}

        {currentSummary.isSummaryAvailable &&
          !isReadonly &&
          !currentSummary.isEmpty && (
            <HiddenForManualCharting>
              <Card className="w-full rounded-xl border-border-primary">
                <CardHeader className="flex h-10 flex-row rounded-t-xl bg-bg-primary p-5">
                  <Stack direction="horizontal" gap={3} align="stretch">
                    <Icon name="WandSparkles" size="xl" />
                    <Heading size="xl" weight="semibold">
                      {t('appointmentPage.improveYourNotes')}
                    </Heading>
                  </Stack>
                </CardHeader>
                <CardContent className="flex flex-col items-start rounded-b-xl bg-bg-primary px-0 py-3 pt-4">
                  {[
                    t('smartBlocks.customInstructions.chipRemoveParts'),
                    t('smartBlocks.customInstructions.chipIncludeDiscussion'),
                    t('smartBlocks.customInstructions.chipMoreConcise'),
                    t('smartBlocks.customInstructions.chipMoreDetails'),
                  ].map((chip, index, chips) => {
                    return (
                      <div key={chip} className="w-full px-2">
                        <Button
                          variant="ghost"
                          onClick={() => {
                            captureEvent(
                              'user_clicked_top_level_improve_notes_chip',
                              {
                                instructions,
                              },
                            );
                            setInstructions(chip);
                            setOpen(true);
                          }}
                        >
                          {chip}
                        </Button>
                        {index !== chips.length - 1 && (
                          <hr className="mx-3 my-1 bg-border-primary" />
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </HiddenForManualCharting>
          )}
      </Stack>
    </Stack>
  );
};

export const AppointmentSummaryResult = () => {
  const { latestSummaryFailureType } = useAppointmentActivity();
  const appointmentId = useAppointmentId();
  const triggerLocalFileReupload = useTriggerLocalFileReupload(appointmentId);
  const kind = useAppointmentSummaryDisplayKind();

  const summaryResult = (
    <DetailsSidebarProvider overlayOnly>
      <SummaryResult data-accessibility-label="summary-results" />
    </DetailsSidebarProvider>
  );

  switch (kind) {
    case 'manual-charting':
    case 'existing-note':
    case 'fallback':
      return summaryResult;
    case 'stuck':
      return <SummaryStuck onBeforeRetry={triggerLocalFileReupload} />;
    case 'pending-audio':
      return <SummaryPendingData />;
    case 'summarizing-taking-longer':
      return <SummaryInProgressTakingLonger />;
    case 'pending-transcript':
    case 'summarizing':
      return <SummaryInProgress />;
    case 'failed-summary':
      // DEGRADED_TRANSCRIPT means a transcript chunk failed — the retry
      // endpoint reschedules failed transcripts and reseeds summarization,
      // so route to the stuck-recovery view instead of the dead-end failure
      // copy.
      if (latestSummaryFailureType === 'DEGRADED_TRANSCRIPT') {
        return <SummaryStuck onBeforeRetry={triggerLocalFileReupload} />;
      }
      return <SummaryFailed failureType={latestSummaryFailureType} />;
  }
};
